import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import { useAppSelector } from "../../../../app/hooks";
import { Loading } from "../../../../components/Loading";
import StatsColorPolygon from "../../../../components/StatsColorPolygon";
import { useAdminReportsFilters } from "../useAdminReportsFilters";
import { getReportLevel, buildReportQueryString, reportSubLevelName } from "../adminReportsUtil";
import { ReportLevel } from "../adminReportsApiSlice";
import { DistrictSchoolFilters } from "../DistrictSchoolFilters";
import { TableRightSideHeader } from "../TableRightSideHeader";
import { UsageSummaryRow, useLazyUsageSummaryQuery } from "./adminUsageReportApiSlice";

export default function UsageSummary() {
	const { aspUrl } = useAppSelector((state) => state.site);
	const {
		maxReportLevel,
		districtOptions,
		schoolOptions,
		districtFilter,
		schoolFilter,
		setDistrictFilter,
		setSchoolFilter,
	} = useAdminReportsFilters();

	const navigate = useNavigate();

	const [data, setData] = useState<UsageSummaryRow[]>();
	const [lastUpdated, setLastUpdated] = useState<Date>();
	const [reportLevel, setReportLevel] = useState<ReportLevel>(maxReportLevel);

	const [callUsageSummary] = useLazyUsageSummaryQuery();

	useEffect(() => {
		// skip if don't have the necessary ids for the max level to prevent auto-fail calls
		if (maxReportLevel === ReportLevel.School && (!districtFilter || !schoolFilter)) {
			return;
		}
		if (maxReportLevel === ReportLevel.District && !districtFilter) {
			return;
		}

		callUsageSummary(
			{
				districtId: districtFilter === 0 ? undefined : districtFilter,
				schoolId: schoolFilter === 0 ? undefined : schoolFilter,
			},
			true //cache the results
		)
			.unwrap()
			.then((payload) => {
				setData(payload.response);
				setLastUpdated(payload.lastUpdateDate);
				setReportLevel(getReportLevel(districtFilter, schoolFilter));
			})
			.catch((error) => {
				console.log(error.data);
				navigate("/adminReports");
			});
	}, [districtFilter, schoolFilter]);

	const queryString = useMemo(() => {
		return buildReportQueryString({
			districtFilter,
			schoolFilter,
		});
	}, [districtFilter, schoolFilter]);

	return (
		<section>
			<h1>{reportLevel} Usage Summary</h1>
			<p className="mt-4 print:hidden">{`The Usage Summary Report shows the usage across the ${reportLevel.toLowerCase()} in writing, peer reviews and lessons. ${schoolOptions.length > 1 || districtOptions.length > 1 ? `Use the filters below to view data for that selection.` : ""} View the report for each usage category by clicking the View Report button on the right side. Click the three dots to print or download.`}</p>
			<div className="mt-3">
				<Loading loading={!data} />
				{data && (
					<>
						<div className="flex justify-between items-end">
							<DistrictSchoolFilters
								districtOptions={districtOptions}
								schoolOptions={schoolOptions}
								districtFilter={districtFilter}
								schoolFilter={schoolFilter}
								setDistrictFilter={setDistrictFilter}
								setSchoolFilter={setSchoolFilter}
							/>
							<TableRightSideHeader
								lastUpdated={lastUpdated}
								hasData={true}
								uniqueKey="usage-summary"
								printLabel="Summary"
								downloadExcelLink={`${aspUrl}/AdminReports/UsageSummaryExcel?${queryString}`}
								downloadCsvLink={`${aspUrl}/AdminReports/UsageSummaryCsv?${queryString}`}
							/>
						</div>
						<div className="relative isolate overflow-hidden mt-3">
							<SectionHeader
								name="Writing Usage"
								link="/adminReports/essaysAndDrafts"
							/>
							<SectionStats data={data.slice(0, 3)} />
						</div>

						<div className="relative isolate overflow-hidden mt-3">
							<SectionHeader
								name="Peer Review Usage"
								link="/adminReports/reviewsUsage"
							/>
							<SectionStats data={data.slice(3, 6)} />
						</div>

						<div className="relative isolate overflow-hidden mt-3">
							<SectionHeader
								name="Lesson Usage"
								link="/adminReports" //update this link after making the lesson reports
							/>
							<SectionStats data={data.slice(6)} isLessonSection />
						</div>
					</>
				)}
			</div>
		</section>
	);
}

interface SectionHeaderProps {
	name: string;
	link: string;
}

function SectionHeader(props: SectionHeaderProps) {
	const navigate = useNavigate();

	return (
		<div className="flex justify-between items-center px-8 py-2">
			<h1 className="text-base font-semibold leading-7">{props.name}</h1>
			<button
				onClick={() => navigate(props.link)}
				className="btn btn-naked btn-small border !shadow-lg print:!hidden">
				<span className="flex items-center text-primary-700">
					View Report <ArrowRightIcon className="size-4 ml-2" />
				</span>
			</button>
		</div>
	);
}

interface SectionStatsProps {
	data: UsageSummaryRow[];
	isLessonSection?: boolean;
}

function SectionStats(props: SectionStatsProps) {
	return (
		<>
			<div className="border-y border-y-gray-900/10">
				<dl
					className={`mx-auto max-w-7xl px-2 grid ${props.isLessonSection ? "grid-cols-1" : "grid-cols-3"}`}>
					{props.data.map((stat, statIdx) => (
						<div
							key={stat.displayName}
							className={`${statIdx == 2 || props.isLessonSection ? "" : "border-r-2"} border-b hover:cursor-default flex flex-wrap items-baseline justify-between gap-y-1 px-6 py-4 border-gray-900/5`}>
							<dt className="text-sm font-medium leading-6 text-gray-600">
								{stat.displayName}
							</dt>
							<dd className="w-full flex gap-x-3 text-3xl items-center font-medium leading-10 tracking-tight">
								{stat.displayName === "Completion %" ? (
									<>
										<div className="print:hidden w-3/4 inline-flex bg-gray-200 rounded-full h-3.5 dark:bg-gray-700 progress-container">
											<div
												className={`${Number(stat.value) < 25 ? "bg-red-500" : Number(stat.value) > 75 ? "bg-green-600" : "bg-yellow-500"} h-3.5 rounded-full progress-bar`}
												style={{
													width: `${stat.value.toString()}%`,
												}}></div>
										</div>
										<p className="w-1/4"> {stat.value}%</p>
									</>
								) : (
									<p>{stat.value}</p>
								)}
							</dd>
						</div>
					))}
				</dl>
			</div>
			<StatsColorPolygon />
		</>
	);
}
